/**
 * Admin Automations page — admins toggle individual automated processes
 * on/off (emails, SMS, AI assists, scheduled jobs). NOT a master switch:
 * each automation has its own toggle so admins can stop one without
 * affecting the others.
 *
 * Route: /admin/automations
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lightning, Envelope, ChatText, Robot, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import AccountMenu from "@/components/AccountMenu";

const CAT_META = {
  email: { icon: Envelope, label_key: "automations.cat.email", tone: "blue" },
  sms: { icon: ChatText, label_key: "automations.cat.sms", tone: "violet" },
  ai: { icon: Robot, label_key: "automations.cat.ai", tone: "emerald" },
  scheduler: { icon: Clock, label_key: "automations.cat.scheduler", tone: "amber" },
};

const TONES = {
  blue: "border-blue-200 bg-blue-50/50",
  violet: "border-violet-200 bg-violet-50/50",
  emerald: "border-emerald-200 bg-emerald-50/50",
  amber: "border-amber-200 bg-amber-50/50",
};

export default function AdminAutomationsPage({ user, onLogout, onLogoutAll }) {
  const t = useT();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], categories: {} });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // per-key spinner

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/automations");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load automations");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const toggle = async (key, currently) => {
    setBusy(b => ({ ...b, [key]: true }));
    try {
      const r = await api.post(`/admin/automations/${key}/toggle`,
                               { enabled: !currently });
      setData(d => ({
        ...d,
        items: d.items.map(it => it.key === key ? r.data : it),
        categories: Object.fromEntries(
          Object.entries(d.categories).map(([cat, list]) => [
            cat, list.map(it => it.key === key ? r.data : it),
          ])
        ),
      }));
      toast.success(`${r.data.label}: ${r.data.enabled ? "ON" : "OFF"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setBusy(b => ({ ...b, [key]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 px-4 md:px-8 h-14 flex items-center gap-3 max-w-5xl mx-auto">
        <button data-testid="auto-back-home" onClick={() => navigate("/")}
          className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"
          title={t("automations.back_home_tooltip")}>
          <ArrowLeft size={18} weight="bold" />
        </button>
        <Lightning size={20} weight="fill" className="text-amber-300" />
        <h1 className="text-base font-black tracking-tighter font-['Chivo']" data-testid="auto-title">
          {t("automations.page_title")}
        </h1>
        <div className="ml-auto"><AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} /></div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <p className="text-sm text-slate-600 max-w-2xl" data-testid="auto-subtitle">
          {t("automations.subtitle")}
        </p>
        {loading && <div className="text-sm text-slate-500">{t("automations.loading")}</div>}
        {!loading && Object.keys(data.categories).map(cat => {
          const meta = CAT_META[cat] || CAT_META.ai;
          const Icon = meta.icon;
          const list = data.categories[cat] || [];
          if (list.length === 0) return null;
          return (
            <section key={cat} data-testid={`auto-cat-${cat}`}>
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2 mb-2">
                <Icon size={16} weight="fill" />
                {t(meta.label_key)} <span className="text-slate-400">({list.length})</span>
              </h2>
              <div className={`border rounded-md divide-y ${TONES[meta.tone]}`}>
                {list.map(it => (
                  <div key={it.key} data-testid={`auto-row-${it.key}`}
                    className="p-4 flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm">{it.label}</span>
                        {!it.default_enabled && (
                          <span className="text-[10px] uppercase tracking-wider bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
                            {t("automations.opt_in")}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-600 mt-1">{it.description}</p>
                      {it.updated_by && (
                        <p className="text-[10px] text-slate-400 mt-1">
                          {t("automations.last_changed", null, {
                            who: it.updated_by,
                            when: new Date(it.updated_at).toLocaleString(),
                          })}
                        </p>
                      )}
                    </div>
                    <button
                      data-testid={`auto-toggle-${it.key}`}
                      onClick={() => toggle(it.key, it.enabled)}
                      disabled={busy[it.key]}
                      role="switch"
                      aria-checked={it.enabled}
                      className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0
                        ${it.enabled ? "bg-emerald-600" : "bg-slate-300"}
                        ${busy[it.key] ? "opacity-50" : "hover:opacity-90"}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform
                        ${it.enabled ? "translate-x-5" : ""}`} />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </main>
    </div>
  );
}
